import { canonicalStringify, fnv1a32 } from '@/core/util/canonical';
import type { BackupRecord, LctDb, LedgerEntry, SaveRecord } from './db';
import { migrate, SaveFromFutureError } from './migrations';
import { zGameSaveV1, type GameSave } from './schema/v1';

export type LoadResult =
  | { kind: 'new' }
  | { kind: 'ok' | 'recovered'; save: GameSave; fromRevision: number }
  | { kind: 'corrupt'; rawJson: string }
  | { kind: 'future'; version: number };

/** Another tab (or a duplicate flush) already wrote a newer revision. */
export class StaleWriteError extends Error {
  override name = 'StaleWriteError';
}

export interface SaveRepositoryOptions {
  now?: () => number;
  /** Take a rolling backup every N revisions… */
  backupEveryRevisions?: number;
  /** …or when the last backup is older than this. */
  backupMinIntervalMs?: number;
  /** Rolling backups kept. pre-* backups are kept for 30 days. */
  backupKeep?: number;
  /** Ledger rows kept (newest). */
  ledgerKeep?: number;
}

const DAY_MS = 86_400_000;

/**
 * Atomic save/load with checksums and rolling backups (content-schemas §11.2–11.3, plan §5.4).
 * Never silently resets: a corrupt main save falls back to the newest valid backup, else reports `corrupt`.
 */
export class SaveRepository {
  private readonly now: () => number;
  private readonly backupEveryRevisions: number;
  private readonly backupMinIntervalMs: number;
  private readonly backupKeep: number;
  private readonly ledgerKeep: number;

  constructor(
    private readonly db: LctDb,
    opts: SaveRepositoryOptions = {},
  ) {
    this.now = opts.now ?? (() => Date.now());
    this.backupEveryRevisions = opts.backupEveryRevisions ?? 10;
    this.backupMinIntervalMs = opts.backupMinIntervalMs ?? 10 * 60_000;
    this.backupKeep = opts.backupKeep ?? 5;
    this.ledgerKeep = opts.ledgerKeep ?? 2000;
  }

  async load(): Promise<LoadResult> {
    const main = await this.db.saves.get('main');
    if (!main) return { kind: 'new' };
    const backups = await this.db.backups.orderBy('revision').reverse().toArray();
    const candidates: SaveRecord[] = [main, ...backups];
    for (const candidate of candidates) {
      const result = this.decode(candidate);
      if (result.kind === 'future') return result;
      if (result.kind === 'ok') {
        return {
          kind: candidate === main ? 'ok' : 'recovered',
          // A backup is older than the broken main record: continue from the main revision, or every
          // following write would be refused as stale and the game could never save again.
          save: { ...result.save, revision: Math.max(result.save.revision, main.revision) },
          fromRevision: candidate.revision,
        };
      }
    }
    return { kind: 'corrupt', rawJson: main.json };
  }

  /**
   * Writes `save` as the next revision, with its ledger rows, in one IndexedDB transaction.
   * Returns the snapshot actually stored (revision + 1, updatedAt = now).
   */
  async write(save: GameSave, ledger: readonly LedgerEntry[] = []): Promise<GameSave> {
    const now = this.now();
    const snapshot: GameSave = { ...save, revision: save.revision + 1, updatedAt: now };
    const json = canonicalStringify(snapshot);
    const record: SaveRecord = {
      slot: 'main',
      saveId: snapshot.saveId,
      schemaVersion: snapshot.schemaVersion,
      revision: snapshot.revision,
      updatedAt: now,
      checksum: fnv1a32(json),
      json,
    };
    await this.db.transaction('rw', [this.db.saves, this.db.backups, this.db.ledger], async () => {
      const current = await this.db.saves.get('main');
      if (current && current.revision >= record.revision) throw new StaleWriteError();
      if (current && (await this.needsBackup(current, now))) {
        await this.db.backups.add({ ...current, reason: 'rolling', createdAt: now });
      }
      await this.db.saves.put(record);
      if (ledger.length > 0) await this.db.ledger.bulkPut([...ledger]);
      await this.prune(now);
    });
    return snapshot;
  }

  /**
   * Replace the save with an imported one (already validated). The current save is backed up first, and the
   * imported snapshot is written as the next revision so open tabs see it as newer.
   */
  async importSave(save: GameSave): Promise<void> {
    await this.backupCurrent('pre-import');
    const current = await this.db.saves.get('main');
    await this.write({ ...save, revision: Math.max(save.revision, current?.revision ?? 0) });
  }

  /** "Bắt đầu lại từ đầu": keep a backup of the current save, then clear the slot so the next boot is fresh. */
  async reset(): Promise<void> {
    await this.db.transaction('rw', [this.db.saves, this.db.backups], async () => {
      await this.backupCurrent('pre-reset');
      await this.db.saves.delete('main');
    });
  }

  /** Explicit backup before risky operations (migration, import, reset). */
  async backupCurrent(reason: Exclude<BackupRecord['reason'], 'rolling'>): Promise<void> {
    const current = await this.db.saves.get('main');
    if (current) await this.db.backups.add({ ...current, reason, createdAt: this.now() });
  }

  private decode(record: SaveRecord): { kind: 'ok'; save: GameSave } | { kind: 'bad' } | LoadResult {
    if (fnv1a32(record.json) !== record.checksum) return { kind: 'bad' };
    try {
      const migrated = migrate(JSON.parse(record.json));
      const parsed = zGameSaveV1.safeParse(migrated);
      if (!parsed.success) {
        console.warn('[save] invalid snapshot', record.revision, parsed.error.issues.slice(0, 5));
        return { kind: 'bad' };
      }
      return { kind: 'ok', save: parsed.data };
    } catch (e) {
      if (e instanceof SaveFromFutureError) return { kind: 'future', version: e.version };
      return { kind: 'bad' };
    }
  }

  private async needsBackup(current: SaveRecord, now: number): Promise<boolean> {
    if (current.revision % this.backupEveryRevisions === 0) return true;
    const last = await this.db.backups.orderBy('createdAt').last();
    return !last || now - last.createdAt >= this.backupMinIntervalMs;
  }

  private async prune(now: number): Promise<void> {
    const rolling = await this.db.backups.where('reason').equals('rolling').sortBy('createdAt');
    const excess = rolling.length - this.backupKeep;
    if (excess > 0) await this.db.backups.bulkDelete(rolling.slice(0, excess).map((b) => b.id ?? -1));
    const oldSpecial = await this.db.backups
      .where('createdAt')
      .below(now - 30 * DAY_MS)
      .filter((b) => b.reason !== 'rolling')
      .primaryKeys();
    if (oldSpecial.length > 0) await this.db.backups.bulkDelete(oldSpecial);
    const ledgerCount = await this.db.ledger.count();
    if (ledgerCount > this.ledgerKeep) {
      const oldest = await this.db.ledger
        .orderBy('atWallMs')
        .limit(ledgerCount - this.ledgerKeep)
        .primaryKeys();
      await this.db.ledger.bulkDelete(oldest);
    }
  }
}
